import { groupDaysByExperience } from '@koob/utils';

interface VersionCompareBlock {
  frame: any | null;
  dayIndices: number[];
}

export function formatPosition(position: any): string | null {
  if (!position) {
    return null;
  }

  const city = position.city || position.name || position.title || '';
  const country = position.country ? `, ${position.country}` : '';

  return city || country ? `${city}${country}` : null;
}

// One block per experience frame (its days inside), and one per day that
// belongs to no experience — the same grouping the live trip uses, so the two
// panels read alike.
function getVersionCompareBlocks(days: any[]): VersionCompareBlock[] {
  const frameByStartDay = new Map<number, any>(
    groupDaysByExperience({ days }).map((frame: any) => [
      frame.startDayIndex,
      frame,
    ]),
  );
  const blocks: VersionCompareBlock[] = [];

  for (let dayIndex = 0; dayIndex < (days?.length ?? 0); dayIndex += 1) {
    const frame = frameByStartDay.get(dayIndex);

    if (frame && frame.dayIndices.length > 0) {
      blocks.push({ frame, dayIndices: frame.dayIndices });
      dayIndex = frame.dayIndices[frame.dayIndices.length - 1];
    } else {
      blocks.push({ frame: null, dayIndices: [dayIndex] });
    }
  }

  return blocks;
}

// A version whose trip carries no start city is announced with the place its
// itinerary actually opens on.
function getArrivalLocation(
  startTitle: string | undefined,
  blocks: VersionCompareBlock[],
  experiences: Record<string, any>,
): string | null {
  if (startTitle) {
    return startTitle;
  }

  const firstFrame = blocks.find((block) => block.frame)?.frame;
  const experience = firstFrame ? experiences?.[firstFrame.experienceId] : null;

  return formatPosition(experience?.programs?.[0]?.startPosition);
}

// The row that closes it: the trip's end city, or the place the last frame of
// the version ends on.
function getDepartureLocation(
  endTitle: string | undefined,
  blocks: VersionCompareBlock[],
  experiences: Record<string, any>,
): string | null {
  if (endTitle) {
    return endTitle;
  }

  const framedBlocks = blocks.filter((block) => block.frame);
  const lastFrame = framedBlocks[framedBlocks.length - 1]?.frame;
  const experience = lastFrame ? experiences?.[lastFrame.experienceId] : null;
  const programs = experience?.programs ?? [];
  const lastProgram =
    programs[(lastFrame?.dayIndices?.length ?? 1) - 1] ??
    programs[programs.length - 1];

  return formatPosition(lastProgram?.endPosition);
}

// Where a frame starts and ends, from its experience's programs.
export function getFrameLocations(experience: any, dayCount: number) {
  const programs = experience?.programs;
  const start = formatPosition(programs?.[0]?.startPosition);
  const lastProgram =
    programs?.[dayCount - 1] ?? programs?.[(programs?.length ?? 1) - 1];
  const end = formatPosition(lastProgram?.endPosition) ?? start;

  return { start, end };
}

// The blocks and the arrival/departure rows of a version.
export function getVersionOutline(
  days: any[],
  experiences: Record<string, any>,
  startTitle: string | undefined,
  endTitle: string | undefined,
) {
  const blocks = getVersionCompareBlocks(days);

  return {
    blocks,
    arrivalLocation: getArrivalLocation(startTitle, blocks, experiences),
    departureLocation: getDepartureLocation(endTitle, blocks, experiences),
  };
}

// Where a hotel stay's rooms were chosen: the first day of the stay holding a
// rooms composition.
export function getStayRooms(days: any[], dayIndex: number) {
  const hotelId = days?.[dayIndex]?.hotelId;

  if (!hotelId) {
    return null;
  }

  let stayStart = dayIndex;
  while (days?.[stayStart - 1]?.hotelId === hotelId) {
    stayStart -= 1;
  }

  for (let idx = stayStart; days?.[idx]?.hotelId === hotelId; idx += 1) {
    if (days[idx]?.roomsComposition?.length) {
      return days[idx].roomsComposition;
    }
  }

  return null;
}
